import React, { useEffect, useMemo, useState } from 'react';
import { FieldTitle, useGetList, useGetMany, useInput, useTranslate } from 'react-admin';
import { DragDropContext, Draggable, Droppable } from '@hello-pangea/dnd';
import debounce from 'lodash/debounce';

import { FormHelperText, IconButton, TextField, Typography } from '@material-ui/core';
import { makeStyles } from '@material-ui/core/styles';
import Autocomplete from '@material-ui/lab/Autocomplete';
import CloseIcon from '@material-ui/icons/Close';
import DragIndicatorIcon from '@material-ui/icons/DragIndicator';

import { reorderOnDragEnd } from 'utils/dragAndDrop';


const useStyles = makeStyles((theme) => ({
	label: {
		marginBottom: theme.spacing(0.5),
	},
	row: {
		display: 'flex',
		alignItems: 'center',
		border: `1px solid ${theme.palette.divider}`,
		borderRadius: theme.shape.borderRadius,
		marginBottom: theme.spacing(1),
		paddingLeft: theme.spacing(0.5),
		paddingRight: theme.spacing(0.5),
		minHeight: 40,
		backgroundColor: theme.palette.background.paper,
	},
	dragHandle: {
		display: 'flex',
		alignItems: 'center',
		marginRight: theme.spacing(0.5),
		color: theme.palette.action.active,
		cursor: 'grab',
	},
	rank: {
		minWidth: 24,
		fontWeight: 700,
	},
	rowName: {
		flexGrow: 1,
	},
}));


const AddItemAutocomplete = ({ options, addItemLabel, onAdd, onQueryChange, disabled }) => {
	const translate = useTranslate();
	const [query, setQuery] = useState('');

	const changeQuery = (newQuery) => {
		setQuery(newQuery);
		if (onQueryChange) onQueryChange(newQuery);
	};

	return (
		<Autocomplete
			options={options}
			getOptionLabel={(choice) => choice.name}
			disabled={disabled}
			// The picked value never persists in the field (value stays null),
			// so the built-in clear icon never shows — hence disableClearable
			// and the manual clear button below
			disableClearable
			value={null}
			inputValue={query}
			onInputChange={(event, newQuery) => changeQuery(newQuery)}
			onChange={(event, choice) => {
				if (choice) onAdd(choice);
				changeQuery('');
			}}
			renderInput={(params) => (
				<TextField
					{...params}
					size="small"
					placeholder={addItemLabel ? `${translate(addItemLabel)}…` : ''}
					InputProps={{
						...params.InputProps,
						endAdornment: (
							<React.Fragment>
								{query !== '' && (
									<IconButton size="small" onClick={() => changeQuery('')}>
										<CloseIcon fontSize="small" />
									</IconButton>
								)}
								{params.InputProps.endAdornment}
							</React.Fragment>
						),
					}}
				/>
			)}
		/>
	);
};


// Ordered id list input: drag rows to set priority (top is highest), add via
// searchable picker, remove per row. Value is an array of choice ids.
// `onAddQueryChange` switches the picker to server-side search: choices are
// expected to follow the query, so the picker never disables itself.
export const OrderedIdsInput = ({ choices = [], addItemLabel, label, source, resource, helperText, onAddQueryChange }) => {
	const classes = useStyles();
	const translate = useTranslate();
	const { input } = useInput({ source });

	const ids = Array.isArray(input.value) ? input.value : [];
	const choiceById = new Map(choices.map((choice) => [choice.id, choice]));
	const getItemName = (id) => (choiceById.has(id) ? choiceById.get(id).name : `#${id}`);
	const availableChoices = choices.filter((choice) => !ids.includes(choice.id));

	const handleDragEnd = (result) => {
		const reordered = reorderOnDragEnd(result, ids);
		if (reordered) input.onChange(reordered);
	};

	return (
		<div>
			{label !== false && (
				<Typography variant="subtitle2" className={classes.label}>
					<FieldTitle label={label} source={source} resource={resource} />
				</Typography>
			)}
			<DragDropContext onDragEnd={handleDragEnd}>
				<Droppable droppableId={source}>
					{(droppableProvided) => (
						<div ref={droppableProvided.innerRef} {...droppableProvided.droppableProps}>
							{ids.map((id, index) => (
								<Draggable key={id} draggableId={String(id)} index={index}>
									{(draggableProvided) => (
										<div
											ref={draggableProvided.innerRef}
											{...draggableProvided.draggableProps}
											className={classes.row}
										>
											<span className={classes.dragHandle} {...draggableProvided.dragHandleProps}>
												<DragIndicatorIcon fontSize="small" />
											</span>
											<Typography className={classes.rank}>{index + 1}</Typography>
											<Typography className={classes.rowName}>{getItemName(id)}</Typography>
											<IconButton
												size="small"
												onClick={() => input.onChange(ids.filter((itemId) => itemId !== id))}
											>
												<CloseIcon fontSize="small" />
											</IconButton>
										</div>
									)}
								</Draggable>
							))}
							{droppableProvided.placeholder}
						</div>
					)}
				</Droppable>
			</DragDropContext>
			<AddItemAutocomplete
				options={availableChoices}
				addItemLabel={addItemLabel}
				onAdd={(choice) => input.onChange([...ids, choice.id])}
				onQueryChange={onAddQueryChange}
				disabled={!onAddQueryChange && availableChoices.length === 0}
			/>
			{helperText && <FormHelperText>{translate(helperText)}</FormHelperText>}
		</div>
	);
};


// OrderedIdsInput backed by a reference resource: the picker searches
// server-side (autocomplete `q` filter, one page of results) and the names of
// the already-picked ids are fetched by id, so the full reference list is
// never preloaded.
export const ReferenceOrderedIdsInput = ({ reference, optionText, ...props }) => {
	const { input } = useInput({ source: props.source });
	const ids = Array.isArray(input.value) ? input.value : [];

	const [query, setQuery] = useState('');
	const setQueryDebounced = useMemo(() => debounce(setQuery, 300), []);
	useEffect(() => () => setQueryDebounced.cancel(), [setQueryDebounced]);

	const { data: selectedRecords } = useGetMany(reference, ids);
	const { data: searchRecordsById, ids: searchIds } = useGetList(
		reference,
		{ page: 1, perPage: 15 },
		{ field: optionText, order: 'ASC' },
		{ q: query },
	);

	const choiceById = new Map();
	(searchIds || []).forEach((id) => {
		const record = searchRecordsById[id];
		if (record) choiceById.set(record.id, record);
	});
	(selectedRecords || []).filter(Boolean).forEach((record) => choiceById.set(record.id, record));
	const choices = Array.from(choiceById.values()).map((record) => ({
		id: record.id,
		name: record[optionText],
	}));

	return <OrderedIdsInput {...props} choices={choices} onAddQueryChange={setQueryDebounced} />;
};
